import { Terminal, Shield, Brain, AlertTriangle, Eye, Users, Coffee } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DonateButton } from "@/components/DonateButton";

const HowItWorks = () => {
  const navigate = useNavigate();

  const questions = [
    { category: "Timing", question: "Does the timing of the story feel suspicious or highly convenient relative to other events?", icon: "⏰" },
    { category: "Emotional Manipulation", question: "Does it provoke fear, outrage, shame, or guilt without strong evidence?", icon: "😱" },
    { category: "Uniform Messaging", question: "Are the same phrases, talking points, or frames echoed across outlets?", icon: "📢" },
    { category: "Missing Information", question: "Are key alternative explanations, data, or context conspicuously absent?", icon: "❓" },
    { category: "Simplistic Narratives", question: "Is the story reduced to a 'good vs. evil' or 'heroes vs. villains' framing?", icon: "⚔️" },
    { category: "Tribal Division", question: "Does it push an 'us vs. them' identity split between groups?", icon: "🔀" },
    { category: "Authority Overload", question: "Are questionable experts or single authorities used to shut down doubt?", icon: "👔" },
    { category: "Call for Urgent Action", question: "Does it demand immediate action/decisions with little time for reflection?", icon: "⚡" },
    { category: "Overuse of Novelty/Shock", question: "Is the event framed as shocking, unprecedented, or 'once in a lifetime' repeatedly?", icon: "💥" },
    { category: "Financial/Political Gain", question: "Do specific powerful actors clearly stand to benefit if the narrative is believed?", icon: "💰" },
    { category: "Suppression of Dissent", question: "Are critics mocked, demonized, or dismissed instead of engaged?", icon: "🚫" },
    { category: "Black-and-White Framing", question: "Are nuanced or middle positions ignored or portrayed as betrayal?", icon: "⚫⚪" },
    { category: "Manufactured Consensus", question: "Does the article imply 'everyone agrees' without showing real debate or evidence?", icon: "🤝" },
    { category: "Anonymity / Shadowy Sources", question: "Does it rely heavily on anonymous or unverifiable sources for key claims?", icon: "👤" },
    { category: "Strategic Leaks / Selective Disclosure", question: "Are partial facts or leaks presented in a way that clearly shapes perception while omitting balancing info?", icon: "📄" },
    { category: "Convenient Scapegoat", question: "Is blame focused on a single group/person in a way that seems overly neat or convenient?", icon: "🎯" },
    { category: "Narrative Repetition Over Time", question: "Does this story fit into a repeated pattern of similar articles pushing the same theme?", icon: "🔁" },
    { category: "Psychological Priming / Fear Cues", question: "Are there repeated references to danger, chaos, collapse, or existential threat?", icon: "⚠️" },
    { category: "Contradictions / Internal Inconsistency", question: "Are there logical gaps or contradictions that go unaddressed?", icon: "🔄" },
    { category: "Information Asymmetry", question: "Is the flow of information clearly one-way, discouraging verification or independent checking?", icon: "📊" }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100">
      {/* Header */}
      <div className="border-b border-cyan-500/20 bg-black/40 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Terminal className="w-8 h-8 text-cyan-400" />
              <div>
                <h1 className="text-2xl font-bold font-mono text-cyan-400 tracking-wider">NARRATIVE SCOUT</h1>
                <p className="text-xs text-gray-500 font-mono">// How It Works</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DonateButton variant="outline" />
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 font-mono"
              >
                BACK
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Section */}
        <Card className="mb-8 border-cyan-500/30 bg-black/60 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-cyan-400" />
              <CardTitle className="text-cyan-400 font-mono text-2xl">OVERVIEW</CardTitle>
            </div>
            <CardDescription className="text-gray-400 text-base">
              Understanding the Psyops Detection System
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-300">
            <p className="leading-relaxed">
              Narrative Scout uses advanced AI analysis to detect potential psychological operations (psyops) and engineered influence in news articles.
              The system evaluates content against <span className="text-cyan-400 font-bold">20 specific indicators</span> developed from behavioral analysis
              and influence operation research.
            </p>
            <p className="leading-relaxed">
              Each article receives a score from <span className="text-cyan-400 font-bold">20-100</span>, with higher scores indicating stronger evidence
              of manipulation techniques. The analysis examines timing, emotional triggers, information gaps, narrative framing, and other psychological
              manipulation tactics commonly used in influence operations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="text-green-400 font-mono font-bold text-lg mb-1">LOW RISK</div>
                <div className="text-gray-400 text-sm">Score: 20-49</div>
                <div className="text-gray-300 text-sm mt-2">Minimal indicators of manipulation</div>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="text-yellow-400 font-mono font-bold text-lg mb-1">MEDIUM RISK</div>
                <div className="text-gray-400 text-sm">Score: 50-70</div>
                <div className="text-gray-300 text-sm mt-2">Several concerning indicators present</div>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="text-red-400 font-mono font-bold text-lg mb-1">HIGH RISK</div>
                <div className="text-gray-400 text-sm">Score: 71-100</div>
                <div className="text-gray-300 text-sm mt-2">Strong evidence of manipulation</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Psyops Definition Section */}
        <Card className="mb-8 border-cyan-500/30 bg-black/60 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Brain className="w-8 h-8 text-cyan-400" />
              <CardTitle className="text-cyan-400 font-mono text-2xl">WHAT ARE PSYOPS?</CardTitle>
            </div>
            <CardDescription className="text-gray-400 text-base">
              Understanding Engineered Influence
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-300">
            <p className="leading-relaxed">
              Psychological operations (psyops) and engineered influence are strategic attempts to manipulate the perceptions, emotions, and behaviors of a target audience. Unlike simple misinformation (accidental errors) or disinformation (deliberate lies), engineered influence often uses true or partially true information, framed in a specific way to trigger a desired psychological response.
            </p>
            <p className="leading-relaxed">
              The goal is not just to deceive, but to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-gray-300">
              <li><span className="text-cyan-400 font-bold">Demoralize:</span> Weaken the target's will to resist or engage.</li>
              <li><span className="text-cyan-400 font-bold">Divide:</span> Create or exacerbate conflicts between groups.</li>
              <li><span className="text-cyan-400 font-bold">Distract:</span> Shift attention away from critical issues.</li>
              <li><span className="text-cyan-400 font-bold">Destabilize:</span> Erode trust in institutions and shared reality.</li>
            </ul>
            <p className="leading-relaxed mt-4">
              Narrative Scout detects the <span className="text-cyan-400 font-bold">techniques</span> used to achieve these goals, such as emotional triggering, logical fallacies, and manufactured consensus.
            </p>
          </CardContent>
        </Card>

        {/* Methodology Section */}
        <Card className="mb-8 border-cyan-500/30 bg-black/60 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Brain className="w-8 h-8 text-cyan-400" />
              <CardTitle className="text-cyan-400 font-mono text-2xl">METHODOLOGY</CardTitle>
            </div>
            <CardDescription className="text-gray-400 text-base">
              Based on behavioral analysis research by Chase Hughes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-300">
            <p className="leading-relaxed">
              This detection system is built on principles from behavioral analysis and influence operation research. The methodology draws from the work of{" "}
              <a
                href="https://chasehughes.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Chase Hughes
              </a>
              , a leading expert in behavioral profiling and psychological influence detection.
            </p>
            <p className="leading-relaxed">
              The 20-question framework examines multiple dimensions of potential manipulation, from timing and emotional triggers to information control
              and narrative construction. Each question is scored on a scale of 1-5, with AI analysis providing detailed justification for each score.
            </p>
          </CardContent>
        </Card>

        {/* The 20 Questions */}
        <Card className="border-cyan-500/30 bg-black/60 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Eye className="w-8 h-8 text-cyan-400" />
              <CardTitle className="text-cyan-400 font-mono text-2xl">THE 20 DETECTION QUESTIONS</CardTitle>
            </div>
            <CardDescription className="text-gray-400 text-base">
              Each article is evaluated against these specific indicators
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              {questions.map((q, idx) => (
                <div
                  key={idx}
                  className="bg-gray-900/50 border border-cyan-500/20 rounded-lg p-4 hover:border-cyan-500/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">{q.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 font-mono text-xs">
                          {idx + 1}
                        </Badge>
                        <h3 className="text-cyan-400 font-mono font-bold text-sm">{q.category}</h3>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">{q.question}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card className="border-cyan-500/30 bg-gradient-to-br from-cyan-900/20 to-cyan-800/10 backdrop-blur-sm mt-12">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-mono text-cyan-400 flex items-center justify-center gap-2">
              <Coffee className="w-6 h-6" />
              SUPPORT NARRATIVESCOUT
            </CardTitle>
            <CardDescription className="text-gray-400 font-mono">
              Help keep this tool free and independent
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-300 max-w-2xl mx-auto">
              NarrativeScout is a free, open-source tool built to empower critical thinking and media literacy.
              Your support helps maintain the infrastructure and continue development of features that help people
              identify engineered influence operations.
            </p>
            <div className="flex justify-center">
              <DonateButton size="lg" className="text-lg px-8 py-6" />
            </div>
            <p className="text-xs text-gray-500 font-mono">
              All donations go directly to hosting, API costs, and development
            </p>
          </CardContent>
        </Card>

        {/* Credit Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm font-mono space-y-2">
          <p>Detection methodology based on research by{" "}
            <a
              href="https://chasehughes.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline"
            >
              Chase Hughes
            </a>
          </p>
          <p>Inspired by{" "}
            <a
              href="https://thewhyfiles.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline"
            >
              The Why Files
            </a>
            {" "}episode:{" "}
            <a
              href="https://www.youtube.com/watch?v=X46H1dEADJU&t=344s"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 underline"
            >
              "Psyops: How the Government is Using Psychological Warfare on You"
            </a>
          </p>
          <p className="mt-2">Built with AI-powered analysis to help identify engineered influence operations</p>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;

